// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngUtilities from "@vanmeter/App/Components/Common/XngUtilities";
import XngZone from "@vanmeter/App/Components/Common/XngZone";
import { xHeaderUpdate } from "@vanmeter/App/Handlers/XNG/Header";
import useSize from "@vanmeter/App/Hooks/useSize";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { isCheckoutPage } from "@vanmeter/App/Store/Selectors/CheckoutSelector";
import { addScrollbarGutter, hasActiveAppOverlay, removeScrollbarGutter } from "@vanmeter/XDL/Utilities";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import { getLogoImageWidget } from "@vanmeter/App/Store/Selectors/HeaderSelectors";
import LazyImage, { LazyImageProps } from "@vanmeter/MobiusOverrides/LazyImage";
import React, { useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    // XNG-Changes
    isCheckout: isCheckoutPage(state),
    mobileSearch: state.xHeader.mobileSearch,
    headerRegisteredMenu: (state as CustomAppState).xHeader.registerMenu?.length || 0,
    headerRegisteredDrawers: (state as CustomAppState).xHeader.drawers.length,
    logoImageWidget: getLogoImageWidget(state),
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    // XNG-Changes
    xHeaderUpdate,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    // XNG-Changes
    topSection?: InjectableCss;
    middleSectionWrapper?: InjectableCss;
    middleSection?: InjectableCss;
    logo?: InjectableCss;
    search?: InjectableCss;
    actions?: InjectableCss;
    mainNavigation?: InjectableCss;
    checkoutWrapper?: InjectableCss;
    printLogo?: InjectableCss;
    printLogoImage?: LazyImageProps;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    // XNG-Changes
    isCheckout,
    mobileSearch,
    headerRegisteredMenu,
    headerRegisteredDrawers,
    logoImageWidget,
    xHeaderUpdate,
}: Props) => {
    // <XNG-Changes>
    const headerRef = useRef<HTMLDivElement>(null);
    const headerHeight = useSize(headerRef)?.height;
    const editMode = useShellContext().isEditing;

    useEffect(() => {
        if (headerHeight) {
            if (typeof document === "undefined") {
                return;
            }
            const cssRoot = document.querySelector(":root") as HTMLElement;
            cssRoot.style.setProperty("--headerHeight", `${headerHeight + 1}px`);
            xHeaderUpdate({ key: "headerHeight", value: headerHeight });
        }
    }, [headerHeight]);

    useEffect(() => {
        if (headerRegisteredMenu > 0) {
            addScrollbarGutter();
            ariaAppHider.hide(document.body);
        } else if (!hasActiveAppOverlay()) {
            removeScrollbarGutter();
            ariaAppHider.show(document.body);
        }
        if (headerRegisteredDrawers) {
            document.body.classList.add("drawer-open");
        } else {
            document.body.classList.remove("drawer-open");
        }
    }, [headerRegisteredMenu, headerRegisteredDrawers]);

    // <XNG-Changes>

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            {/* XNG-Changes: add logo for print view */}
            {logoImageWidget && (
                <StyledWrapper {...styles.printLogo}>
                    <LazyImage
                        src={logoImageWidget.printLogoImage || logoImageWidget.logoImage}
                        altText={translate("Home")}
                        {...styles.printLogoImage}
                        disableLazyLoad={true}
                        height="100%"
                        width="100%"
                    />
                </StyledWrapper>
            )}
            {/* XNG-Changes */}
            <Page as="header" isHeader={true} {...styles.page} {...{ isCheckout }}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    {/* XNG-Changes: structure zones */}
                    <StyledWrapper id="header-container" ref={headerRef}>
                        {!isCheckout && (
                            <>
                                {(!mobileSearch || editMode) && (
                                    <>
                                        <XngZone
                                            contentId={id}
                                            zoneName="GlobalSiteMessage"
                                            hint="Zone for Global Site Message"
                                        />
                                        <StyledWrapper {...styles.container}>
                                            <StyledWrapper {...styles.topSection}>
                                                <XngZone hint="Top Section" contentId={id} zoneName="topSection" />
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.middleSectionWrapper}>
                                                <StyledWrapper {...styles.middleSection}>
                                                    <StyledWrapper {...styles.logo} data-text-selector="logo">
                                                        <XngZone hint="Logo" contentId={id} zoneName="Logo" />
                                                    </StyledWrapper>
                                                    <StyledWrapper
                                                        {...styles.search}
                                                        className="hide-on-tablet"
                                                        data-text-selector="search"
                                                    >
                                                        <XngZone hint="Search" contentId={id} zoneName="Search" />
                                                    </StyledWrapper>
                                                    <StyledWrapper {...styles.actions} data-text-selector="actions">
                                                        <XngZone hint="Actions" contentId={id} zoneName="Actions" />
                                                    </StyledWrapper>
                                                </StyledWrapper>
                                            </StyledWrapper>
                                            <StyledWrapper
                                                {...styles.mainNavigation}
                                                data-text-selector="main-navigation"
                                            >
                                                <XngZone
                                                    hint="Main Navigation"
                                                    contentId={id}
                                                    zoneName="MainNavigation"
                                                />
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </>
                                )}
                                <StyledWrapper
                                    {...styles.search}
                                    className="show-on-tablet"
                                    data-text-selector="search"
                                >
                                    <XngZone hint="Search" contentId={id} zoneName="Search" />
                                </StyledWrapper>
                            </>
                        )}
                        {isCheckout && (
                            <StyledWrapper {...styles.checkoutWrapper}>
                                <StyledWrapper {...styles.logo} {...{ isCheckout }} data-text-selector="logo">
                                    <XngZone hint="Logo" contentId={id} zoneName="Logo" />
                                </StyledWrapper>
                                <XngZone hint="Checkout Link" contentId={id} zoneName="CheckoutLink" />
                            </StyledWrapper>
                        )}
                        {editMode && <XngZone hint="Checkout Link" contentId={id} zoneName="CheckoutLink" />}
                    </StyledWrapper>
                    <Zone contentId={id} zoneName="Breadcrumb" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
            {/* XNG-Changes */}
            <XngUtilities />
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
